import React from "react";
import styled from "styled-components";
import { Box, Button, Stack } from "@mui/material";
import AdadotLink from "src/components/link";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Auth from "src/services/auth";
import { useAuth } from "src/components/providers/auth-context";
import DemoBadge from "../demo-badge";

const Link = styled(AdadotLink).attrs({ variant: "plain" })`
    height: 60px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-right: ${({ theme }) => theme.spacing(5)};
    padding-left: ${({ theme }) => theme.spacing(5)};

    &:hover {
        color: ${({ theme }) => theme.palette.primary.main};
    }

    &.active {
        color: ${({ theme }) => theme.palette.primary.main};
    }
`;

const LinkContainerMobile = styled.div`
    list-style: none;
    display: flex;
    flex-flow: row nowrap;

    ${({ theme }) => theme.breakpoints.down("md")} {
        flex-flow: column nowrap;
        background-color: ${({ theme }) => theme.palette.background.overlay};
        position: fixed;
        transform: ${({ open }) => (open ? "translateX(0)" : "translateX(100%)")};
        transition: all 0.3s ease-in-out;
        top: 70px;
        right: 0;
        height: 100vh;
        width: 100vw;
        padding-top: 3.5rem;
    }
`;

const LinkButtonContainer = styled(Button)`
    font-size: 12px;
    font-weight: 400;
    height: 100%;
    text-transform: none;
    cursor: pointer;
    color: ${({ theme }) => theme.palette.text.primary};
    min-height: 10vh;

    &:hover {
        color: ${({ theme }) => theme.palette.primary.main};
        background-color: transparent;
    }

    &.active {
        color: ${({ theme }) => theme.palette.primary.main};
    }
`;

const CheeseBurgerOverlay = ({ open, setOpen, isDisemboweled }) => {
    const location = useLocation();
    const { t } = useTranslation();
    const { isUserPayingCustomer, isDemo } = useAuth();
    const headerSub = isUserPayingCustomer ? "subscription" : "upgrade";

    if (isDisemboweled) {
        return (
            <LinkContainerMobile open={open}>
                <Button
                    onClick={() => {
                        Auth.logout(false, false);
                    }}
                    style={{ width: "100%", height: "100%" }}
                    color="secondary"
                >
                    Sign out
                </Button>
            </LinkContainerMobile>
        );
    }
    return (
        <LinkContainerMobile open={open}>
            <Box flexShrink={0} minWidth={0} style={{ position: "relative", zIndex: 5 }}>
                <LinkButtonContainer
                    onClick={() => setOpen(!setOpen)}
                    component={Link}
                    className={location.pathname.startsWith("/overview") ? "active" : null}
                    to="/overview"
                    data-testid="causal-link"
                >
                    {t("header.overview")}
                </LinkButtonContainer>
            </Box>
            <Box flexShrink={0} minWidth={0} style={{ position: "relative", zIndex: 5 }}>
                <LinkButtonContainer
                    onClick={() => setOpen(!setOpen)}
                    component={Link}
                    className={location.pathname.startsWith("/causal") ? "active" : null}
                    to="/causal"
                    data-testid="causal-link"
                >
                    {t("header.causal")}
                </LinkButtonContainer>
            </Box>
            <Box flexShrink={0} minWidth={0} style={{ position: "relative", zIndex: 5 }}>
                <LinkButtonContainer
                    onClick={() => setOpen(!setOpen)}
                    component={Link}
                    className={
                        location.pathname.startsWith("/navigation") ||
                        location.pathname.startsWith("/dashboard") ||
                        location.pathname.startsWith("/repo-view") ||
                        location.pathname.startsWith("/teams/")
                            ? "active"
                            : null
                    }
                    to="/navigation"
                    data-testid="dashboard-link"
                >
                    {t("header.dashboards")}
                </LinkButtonContainer>
            </Box>
            <Box flexShrink={0} minWidth={0} style={{ position: "relative", zIndex: 5 }}>
                <LinkButtonContainer
                    onClick={() => setOpen(!setOpen)}
                    component={Link}
                    className={location.pathname.startsWith("/personal-profile") ? "active" : null}
                    to="/personal-profile"
                    data-testid="profile-link"
                >
                    {t("header.profile")}
                </LinkButtonContainer>
            </Box>
            <Box flexShrink={0} minWidth={0} style={{ position: "relative", zIndex: 5 }}>
                <Stack
                    height="100%"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                >
                    <LinkButtonContainer
                        onClick={() => setOpen(!setOpen)}
                        component={Link}
                        className={location.pathname === "/teams" ? "active" : null}
                        to="/teams"
                        data-testid="teams-link"
                    >
                        {t("header.teams")}
                    </LinkButtonContainer>
                    <DemoBadge style={{ marginLeft: "-32px" }} />
                </Stack>
            </Box>
            <Box flexShrink={0} minWidth={0} style={{ position: "relative", zIndex: 5 }}>
                <Stack
                    height="100%"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                >
                    <LinkButtonContainer
                        onClick={() => setOpen(!setOpen)}
                        component={Link}
                        className={location.pathname.startsWith("/repo-view") ? "active" : null}
                        to="/repo-view"
                        data-testid="repo-view-link"
                    >
                        {t("header.repo-view")}
                    </LinkButtonContainer>
                    <DemoBadge style={{ marginLeft: "-32px" }} />
                </Stack>
            </Box>
            <Box flexShrink={0} minWidth={0} style={{ position: "relative", zIndex: 5 }}>
                <LinkButtonContainer
                    onClick={() => setOpen(!setOpen)}
                    component={Link}
                    className={location.pathname.startsWith("/integrations") ? "active" : null}
                    to="/integrations"
                    data-testid="integrations-link"
                >
                    {t("header.integrations")}
                </LinkButtonContainer>
            </Box>
            <Box flexShrink={0} minWidth={0} style={{ position: "relative", zIndex: 5 }}>
                <LinkButtonContainer
                    onClick={() => setOpen(!setOpen)}
                    component={Link}
                    className={location.pathname.startsWith("/achievements") ? "active" : null}
                    to="/achievements"
                    data-testid="profile-link"
                >
                    {t("header.achievements")}
                </LinkButtonContainer>
            </Box>
            <Box flexShrink={0} minWidth={0} style={{ position: "relative", zIndex: 5 }}>
                <LinkButtonContainer
                    onClick={() => setOpen(!setOpen)}
                    component={Link}
                    className={location.pathname.startsWith("/achievements") ? "active" : null}
                    to="/summary"
                    data-testid="profile-link"
                >
                    {t("header.summary")}
                </LinkButtonContainer>
            </Box>
            {!isDemo && (
                <Box flexShrink={0} minWidth={0} style={{ position: "relative", zIndex: 5 }}>
                    <LinkButtonContainer
                        onClick={() => setOpen(!setOpen)}
                        component={Link}
                        className={location.pathname.startsWith("/profile") ? "active" : null}
                        to="/profile"
                        data-testid="profile-link"
                    >
                        {t("header.profile")}
                    </LinkButtonContainer>
                </Box>
            )}
            <Box flexShrink={0} minWidth={0} style={{ position: "relative", zIndex: 5 }}>
                <LinkButtonContainer
                    onClick={() => setOpen(!setOpen)}
                    component={Link}
                    className={location.pathname.startsWith(`/${headerSub}`) ? "active" : null}
                    to={`/${headerSub}`}
                    data-testid={`${headerSub}-link`}
                >
                    {t(`header.${headerSub}`)}
                </LinkButtonContainer>
            </Box>
            <Button
                onClick={() => {
                    if (!isDemo) {
                        Auth.logout(false, false);
                    } else {
                        Auth.redirectToLogin();
                    }
                }}
                style={{ width: "100%", height: "100%" }}
                color="secondary"
            >
                {isDemo ? "Register Now" : "Sign out"}
            </Button>
        </LinkContainerMobile>
    );
};

export default CheeseBurgerOverlay;
